/**
 * Offline-Aware API Service
 * Wraps API calls to handle offline scenarios with automatic queueing
 */

import { 
  saveOfflineIncident, 
  addToSyncQueue,
  cacheData,
  getCachedData
} from './offlineStorage';
import { isOnline, requestBackgroundSync } from './syncManager';
import { deliverIncident } from './submissionQueue';
import { requireSession, sessionIsCurrent, operationId } from './submissionSession';

const API_BASE = '/api';
let readToken = null, readNonce = null;

/**
 * Make an API request with offline handling
 */
const offlineAwareRequest = async (url, options = {}, offlineConfig = {}) => {
  const { 
    queueIfOffline = false,
    cacheKey = null,
    cacheResponse = false,
    fallbackToCache = true,
    operationType = null 
  } = offlineConfig;

  const session = requireSession(options.headers?.Authorization?.replace(/^Bearer /, ''));
  if ((options.method || 'GET') === 'GET') {
    if (readToken !== session.token) { readToken = session.token; readNonce = operationId(); }
    url += (url.includes('?') ? '&' : '?') + '__cad_read=' + readNonce;
  }
  // If online, try the request
  if (isOnline()) {
    try {
      const response = await fetch(url, options);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = response.status !== 204 ? await response.json() : null;
      
      // Cache successful GET responses if configured
      if (cacheResponse && cacheKey && data) {
        await cacheData(cacheKey, data, session.ownerUserId);
      }

      if (!sessionIsCurrent(session)) throw new Error('Account changed while loading data.');
      return { success: true, data, fromCache: false };
    } catch (error) {
      // Network error - might be going offline
      if (!navigator.onLine && fallbackToCache && cacheKey) {
        const cached = await getCachedData(cacheKey, session.ownerUserId);
        if (cached) {
          if (!sessionIsCurrent(session)) throw new Error('Account changed while reading cached data.');
          return { success: true, data: cached, fromCache: true };
        }
      }
      throw error;
    }
  }

  // Offline handling
  if (queueIfOffline && options.method && options.method !== 'GET') {
    // Queue mutation operations for later sync
    const queueItem = await addToSyncQueue({
      type: operationType || options.method,
      method: options.method,
      url,
      body: options.body ? JSON.parse(options.body) : null
    }, session.token);

    // Request background sync if supported
    requestBackgroundSync().catch(console.error);

    return { 
      success: true, 
      queued: true, 
      queueId: queueItem.id,
      message: 'Saved on this device for review before sending'
    };
  }

  // For GET requests, try cache
  if (fallbackToCache && cacheKey) {
    const cached = await getCachedData(cacheKey, session.ownerUserId);
    if (cached) {
      if (!sessionIsCurrent(session)) throw new Error('Account changed while reading cached data.');
          return { success: true, data: cached, fromCache: true };
    }
  }

  throw new Error('Offline and no cached data available');
};

/**
 * Create authorization headers
 */
const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token && { 'Authorization': `Bearer ${token}` })
});

// ============ INCIDENTS API ============

/**
 * Create a new incident (with offline support)
 */
export const createIncident = async (incidentData, token = localStorage.getItem('rv_token'), { uploads = [] } = {}) => {
  const session = requireSession(token);
  let local;
  try { local = await saveOfflineIncident(incidentData, token, uploads); }
  catch { throw new Error('This device could not save a local copy. The call has not been sent. Keep the form open and restore browser storage before retrying.'); }
  let result;
  try { result = await deliverIncident(local.localId, token, { interactive: incidentData.submissionKind === 'waze' }); }
  catch (error) { result = { accepted: false, record: local, message: 'Saved locally. ' + error.message }; }
  requestBackgroundSync().catch(() => {});
  if (!sessionIsCurrent(session)) return { success: true, pending: true, offline: true, localId: local.localId, uploadLocalIds: local.uploadLocalIds,
    incident: result.record, message: 'Session changed. This call and its delivery receipt are retained for the original account.' };
  return { success: true, pending: !result.accepted, offline: !result.accepted, localId: local.localId, uploadLocalIds: local.uploadLocalIds,
    incident: result.accepted ? result.incident : result.record,
    response: result.accepted ? result.response : undefined,
    replayed: result.accepted ? Boolean(result.replayedLocally || result.response?.replayed) : false,
    message: result.accepted ? 'Call confirmed by the server.' : result.message || 'Call saved on this device; delivery is pending.' };
};

export const createBackdatedIncident = (data, token = localStorage.getItem('rv_token')) =>
  createIncident({ ...data, submissionKind: 'backdated', source: 'BACKDATED' }, token);

export const createWazeIncident = (alert, details = {}, token = localStorage.getItem('rv_token')) =>
  createIncident({ ...details, submissionKind: 'waze', wazeAlertId: alert.id,
    occurredAtUtc: details.occurredAtUtc || new Date().toISOString(), wazeAlertSnapshot: alert }, token);

/**
 * Get incidents list (with caching)
 */
export const getIncidents = async (token, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_BASE}/incidents${queryString ? `?${queryString}` : ''}`;
  const cacheKey = `incidents-${queryString || 'all'}`;

  return offlineAwareRequest(url, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey,
    cacheResponse: true,
    fallbackToCache: true
  });
};

/**
 * Get single incident
 */
export const getIncident = async (id, token) => {
  return offlineAwareRequest(`${API_BASE}/incidents/${id}`, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey: `incident-${id}`,
    cacheResponse: true,
    fallbackToCache: true
  });
};

/**
 * Update incident
 */
export const updateIncident = async (id, incidentData, token) => {
  return offlineAwareRequest(`${API_BASE}/incidents/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(incidentData)
  }, {
    queueIfOffline: true,
    operationType: 'update-incident'
  });
};

/**
 * Close incident
 */
export const closeIncident = async (id, closeData, token) => {
  return offlineAwareRequest(`${API_BASE}/incidents/${id}/close`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(closeData)
  }, {
    queueIfOffline: true,
    operationType: 'close-incident'
  });
};

// ============ STATUS API ============

/**
 * Update user status
 */
export const updateStatus = async (statusData, token) => {
  return offlineAwareRequest(`${API_BASE}/status`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(statusData)
  }, {
    queueIfOffline: true,
    operationType: 'status-update'
  });
};

/**
 * Send GPS ping
 */
export const sendGpsPing = async (pingData, token) => {
  // GPS pings are less critical - don't queue if offline
  if (!isOnline()) {
    return { success: false, offline: true, message: 'GPS ping skipped - offline' };
  }

  const response = await fetch(`${API_BASE}/status/ping`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(pingData)
  });

  if (!response.ok) {
    throw new Error(`GPS ping failed: ${response.statusText}`);
  }

  return { success: true, data: await response.json() };
};

// ============ USERS API ============

/**
 * Get users list (cached for offline reference)
 */
export const getUsers = async (token) => {
  return offlineAwareRequest(`${API_BASE}/users`, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey: 'users-list',
    cacheResponse: true,
    fallbackToCache: true
  });
};

/**
 * Get current user
 */
export const getCurrentUser = async (token) => {
  return offlineAwareRequest(`${API_BASE}/auth/me`, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey: 'current-user',
    cacheResponse: true,
    fallbackToCache: true
  });
};

// ============ MESSAGES API ============

/**
 * Send message (with offline queuing)
 */
export const sendMessage = async (messageData, token) => {
  return offlineAwareRequest(`${API_BASE}/messages`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(messageData)
  }, {
    queueIfOffline: true,
    operationType: 'send-message'
  });
};

/**
 * Get messages (cached)
 */
export const getMessages = async (token) => {
  return offlineAwareRequest(`${API_BASE}/messages`, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey: 'messages',
    cacheResponse: true,
    fallbackToCache: true
  });
};

// ============ UTILITY EXPORTS ============

export { isOnline };

export default {
  createIncident,
  getIncidents,
  getIncident,
  updateIncident,
  closeIncident,
  updateStatus,
  sendGpsPing,
  getUsers,
  getCurrentUser,
  sendMessage,
  getMessages,
  isOnline
};
